import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { useIdlingWorstPerformers } from "../hooks/useIdleReportsHooks";
import { IdleReportsHookParams } from "../hooks/useIdleReportsHooks";
import { PerformersTable } from "../common/PerformersTable";
import type { WorstPerformersDto } from "../../../common/data/service-proxies";
import "../fuel-economy-report/FuelEconomyReport.css";

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

/** Backend returns idle time in seconds; convert to minutes for display. */
const secondsToMinutes = (seconds: number | null | undefined): number | null =>
  seconds != null && !Number.isNaN(seconds) ? seconds / 60 : null;

export const IdleWorstPerformers: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const { data, loading: isLoading, error } = useIdlingWorstPerformers({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;
  const rows = data ?? [];

  const renderValueCell = (row: WorstPerformersDto) => {
    const minutes = secondsToMinutes(row.idleTime);
    return (
      <Typography variant="body2">
        {minutes != null ? `${formatNumber(minutes)} min` : "-"}
      </Typography>
    );
  };

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("common.worstPerformers", {
            _: "Worst performers",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <PerformersTable<WorstPerformersDto>
            rows={rows}
            valueColumnHeader="idleReport.idleTime"
            renderValueCell={renderValueCell}
            getRowKey={(row, index) =>
              `${row.vehicleName ?? ""}-${row.driverFullName ?? ""}-${index}`
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default IdleWorstPerformers;
